import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

/**
 * Feature Spotlight — 1 つのトピックを腰を据えて紹介する中盤コンテンツモジュール。
 *
 * 用途:
 *  - 製品 / 機能 / モジュールの詳細紹介
 *  - 「特に価値の高いポイント」「知っておきたいポイント」など、サブ見出し付きの長文 + CTA
 *  - 雑誌の特集記事的なリッチコンテンツ
 *
 * blocks 配列で「サブ見出し + 本文」を繰り返し記述できる。
 *
 * 4 variants:
 *   image_left_richtext   — 左に画像、右にリッチテキスト + CTA
 *   image_right_richtext  — 反転（交互配置でリズム作り）
 *   image_top_richtext    — 上に画像、下に中央寄せリッチテキスト
 *   floating_card         — 背景画像に半透明カードを浮かせる（プレミアム感）
 */
export const featureSpotlightAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: true, max: 80 },
  { name: 'subheadline', type: 'string', required: false, max: 200, hint: 'タグライン的な短い補足' },
  { name: 'intro', type: 'string', required: false, max: 400, hint: '導入段落（本文の冒頭）' },
  {
    name: 'blocks',
    type: 'array<object>',
    required: false,
    fields: [
      { name: 'heading', type: 'string', required: false, max: 60, hint: 'サブ見出し（任意）' },
      { name: 'body', type: 'string', required: true, max: 600, hint: '段落本文（改行可）' },
    ],
  },
  { name: 'image', type: 'image_url', required: false },
  {
    name: 'imageFit',
    type: 'string',
    required: false,
    max: 10,
    hint: '"cover" (枠に合わせ切取り) / "contain" (全体表示+余白) / "natural" (画像比率を維持)',
  },
  { name: 'primaryCta', type: 'string', required: false, max: 24 },
  { name: 'primaryCtaHref', type: 'url', required: false },
];

export const featureSpotlightUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'Eyebrow', max: 30 },
  { name: 'headline', type: 'text', label: '見出し', required: true, max: 80 },
  { name: 'subheadline', type: 'textarea', label: 'タグライン / サブ見出し', max: 200 },
  { name: 'intro', type: 'textarea', label: '導入段落', max: 400 },
  { name: 'blocks', type: 'spotlight_blocks', label: 'コンテンツブロック (サブ見出し + 本文)' },
  { name: 'image', type: 'image', label: '画像' },
  { name: 'imageFit', type: 'select', label: '画像の表示方法', options: [
    { value: 'cover', label: 'カバー (枠に合わせて切り取り、現行デフォルト)' },
    { value: 'contain', label: 'コンテイン (画像全体を表示、余白あり)' },
    { value: 'natural', label: 'ナチュラル (画像比率を維持、枠が伸縮)' },
  ] },
  { name: 'primaryCta', type: 'text', label: 'CTA ラベル', max: 24 },
  { name: 'primaryCtaHref', type: 'cta_url', label: 'CTA リンク' },
];

const blockSchema = z.object({
  heading: z.string().max(60).optional().nullable(),
  body: z.string().max(600),
});

export const featureSpotlightZodSchema = z.object({
  eyebrow: z.string().max(30).optional().nullable(),
  headline: z.string().min(1).max(80),
  subheadline: z.string().max(200).optional().nullable(),
  intro: z.string().max(400).optional().nullable(),
  blocks: z.array(blockSchema).max(8).optional().nullable(),
  image: z.string().optional().nullable(),
  imageFit: z.enum(['cover', 'contain', 'natural']).optional().nullable(),
  primaryCta: z.string().max(24).optional().nullable(),
  primaryCtaHref: z.string().optional().nullable(),
});

export type FeatureSpotlightContent = z.infer<typeof featureSpotlightZodSchema>;

export const featureSpotlightSample: FeatureSpotlightContent = {
  eyebrow: '新機能',
  headline: '新しい TimeWaver QI Module',
  subheadline: '〜情報場分析をより深く行うための高精度・高性能モジュール〜',
  intro:
    'TimeWaver QI Module は、最先端 AI 技術と情報場技術を融合し、より高度で実践的な分析環境を実現します。\n\nクライアント様一人ひとりのテーマに基づき、複雑な関連性を短時間で捉え、情報場の中で分析。その結果を分かりやすく整理し、実践に活かしやすい形で提示します。',
  blocks: [
    {
      heading: '特に価値の高いポイント',
      body: '自動生成される「概要」機能により、複雑な分析結果も分かりやすく整理され、クライアント様にも伝えやすくなります。\n\nまた、ストレスや制限につながる内容が読み取られた場合は、前向きで活性化を促すアファメーションへと変換され、調和のサポートにすぐ活用できます。',
    },
    {
      heading: '知っておきたいポイント',
      body: 'TimeWaver AI Assistant が、文章作成・要約・解釈などを補助する AI 拡張機能であるのに対し、TimeWaver QI は独立したフル機能モジュールです。\n\n自動データベース生成、関連性分析、結果の整理・提示まで、情報場分析全体のプロセスを再構築し、より明確で実践的な分析体験を提供します。',
    },
  ],
  image: null,
  imageFit: 'cover',
  primaryCta: '詳細はこちら',
  primaryCtaHref: '#',
};
